import { useState, type FormEvent } from 'react';
import { Modal } from './modals';
import { useAuth } from '../auth/AuthContext';
import { v } from '../constants';
import { apiFetch } from '../lib/api';

type Mode = 'login' | 'signup' | 'forgot';

const ERROR_COPY: Record<string, string> = {
  email_in_use: 'An account with that email already exists.',
  displayName_in_use: 'That display name is taken. Try another.',
  displayName_too_short: 'Display name must be at least 3 characters.',
  invalid_credentials: 'Email or password is incorrect.',
  invalid_input: 'Check your fields — display name needs at least 3 characters, password at least 8.',
  request_failed: 'Something went wrong — try again.',
  unauthenticated: 'You\'ve been signed out — please sign in again.',
  rate_limited: 'Too many attempts. Try again in a few minutes.',
};

export function AuthModal({ onClose, initialMode = 'login' }: { onClose: () => void; initialMode?: Mode }) {
  const auth = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Forgot-password success state. We always show the same copy on
  // success — including when the email isn't on file — to avoid leaking
  // account existence.
  const [forgotSent, setForgotSent] = useState(false);
  // "X invited you" eyebrow — sourced from localStorage which App.tsx
  // populates from the `?from=` invite-link URL param on mount. Read
  // once when the modal mounts; if the user signs up successfully we'd
  // ideally clear the key, but leaving it stale is harmless (it only
  // ever shows on the signup tab).
  const invitedBy = (() => {
    try { return localStorage.getItem('hm.invitedBy') || null; }
    catch { return null; }
  })();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'forgot') {
        const r = await apiFetch('/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        if (r.ok) {
          setForgotSent(true);
        } else {
          const body = await r.json().catch(() => ({}));
          setError(ERROR_COPY[body.error] ?? body.error ?? 'request_failed');
        }
        return;
      }
      const res = mode === 'login'
        ? await auth.login(email, password)
        : await auth.signup(email, password, displayName.trim() || (email.split('@')[0] + '___').slice(0, 30));
      if (res.ok) {
        // Clean up the invite-link cookie crumb on successful signup
        // so a later "Create another account" doesn't show a stale
        // inviter that no longer makes sense.
        if (mode === 'signup') {
          try { localStorage.removeItem('hm.invitedBy'); } catch {/* ignore */}
        }
        onClose();
      } else {
        setError(ERROR_COPY[res.error] ?? res.error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const showGoogle = auth.oauthProviders.google;
  const showApple = auth.oauthProviders.apple;
  const showAnyOAuth = showGoogle || showApple;

  return (
    <Modal
      onClose={onClose}
      ariaLabel={
        mode === 'login' ? 'Sign in' :
        mode === 'signup' ? 'Create account' :
        'Reset password'
      }
    >
      <h2>{
        mode === 'login' ? 'Welcome back' :
        mode === 'signup' ? 'Create account' :
        'Reset password'
      }</h2>
      {/* Invite eyebrow — set when a friend's invite link landed the
          user here. Reads from localStorage (App.tsx writes it from
          the ?from= URL param). Only relevant for signup mode; when
          they sign in normally we don't crowd the heading. */}
      {mode === 'signup' && invitedBy && (
        <p style={{
          color: v('accent'),
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          margin: '0 0 8px',
        }}>
          {invitedBy} invited you
        </p>
      )}
      <p style={{ color: v('inkMute'), fontSize: 13, marginBottom: 4 }}>
        {mode === 'login'
          ? 'Sign in to track your streak across devices.'
          : mode === 'signup'
          ? "We'll send you a verification email after signup."
          : "Enter your email and we'll send you a link to set a new password."}
      </p>

      {/* Mode tabs only on login + signup. Forgot mode is reached via
          the "Forgot password?" link below the password field; back to
          login is the modal's natural recovery path. */}
      {mode !== 'forgot' && (
        <div style={{ display: 'flex', gap: 6, margin: '12px 0 18px' }}>
          <button
            type="button"
            onClick={() => { setMode('login'); setError(null); }}
            className="hm-action"
            style={mode === 'login' ? { background: v('ink'), color: v('paper'), borderColor: v('ink') } : {}}
          >Sign in</button>
          <button
            type="button"
            onClick={() => { setMode('signup'); setError(null); }}
            className="hm-action"
            style={mode === 'signup' ? { background: v('ink'), color: v('paper'), borderColor: v('ink') } : {}}
          >Create account</button>
        </div>
      )}

      {/* Forgot-password success card. Same copy regardless of whether
          the address exists — anti-enumeration. */}
      {mode === 'forgot' && forgotSent && (
        <div
          style={{
            padding: 16,
            margin: '12px 0',
            borderRadius: 10,
            background: v('paperDeep'),
            border: `1px solid ${v('ruleSoft')}`,
            fontSize: 13,
            lineHeight: 1.5,
            color: v('inkSoft'),
          }}
        >
          <strong style={{ color: v('ink') }}>Check your email.</strong>
          <p style={{ margin: '6px 0 0', color: v('inkSoft') }}>
            If <span style={{ color: v('ink') }}>{email}</span> matches an
            account, we sent a password-reset link. It expires in 1 hour.
            Didn't get it? Check spam, then{' '}
            <button
              type="button"
              onClick={() => { setForgotSent(false); }}
              className="hm-action"
              style={{ padding: '2px 8px', fontSize: 11 }}
            >try again</button>.
          </p>
          <div style={{ marginTop: 14 }}>
            <button
              type="button"
              onClick={() => { setMode('login'); setForgotSent(false); setError(null); }}
              className="hm-action"
            >Back to sign in</button>
          </div>
        </div>
      )}

      {mode !== 'forgot' && showAnyOAuth && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {showGoogle && (
              <a
                href="/api/v1/auth/oauth/google"
                className="hm-action"
                style={{ ...oauthBtnStyle(), justifyContent: 'center' }}
              >
                <GoogleGlyph /> Continue with Google
              </a>
            )}
            {showApple && (
              <a
                href="/api/v1/auth/oauth/apple"
                className="hm-action"
                style={{ ...oauthBtnStyle(), justifyContent: 'center' }}
              >
                <AppleGlyph /> Continue with Apple
              </a>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 16px', color: v('inkMute'), fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            <div style={{ flex: 1, height: 1, background: v('ruleSoft') }} />
            <span>or with email</span>
            <div style={{ flex: 1, height: 1, background: v('ruleSoft') }} />
          </div>
        </>
      )}

      {!(mode === 'forgot' && forgotSent) && (
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {mode === 'signup' && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: v('inkSoft') }}>Display name</span>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              autoComplete="name"
              minLength={3}
              maxLength={30}
              placeholder="3–30 characters, unique"
              style={inputStyle()}
            />
          </label>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: v('inkSoft') }}>
            {mode === 'login' ? 'Email or username' : 'Email'}
          </span>
          <input
            type={mode === 'login' ? 'text' : 'email'}
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete={mode === 'login' ? 'username' : 'email'}
            maxLength={255}
            style={inputStyle()}
          />
        </label>
        {mode !== 'forgot' && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: v('inkSoft'), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Password</span>
              {mode === 'login' && (
                <button
                  type="button"
                  onClick={() => { setMode('forgot'); setError(null); setPassword(''); }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: v('inkSoft'),
                    fontSize: 11,
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >Forgot password?</button>
              )}
            </span>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={mode === 'signup' ? 8 : 1}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              style={inputStyle()}
            />
            {mode === 'signup' && (
              <span style={{ fontSize: 11, color: v('inkMute') }}>At least 8 characters.</span>
            )}
          </label>
        )}

        {error && (
          <div style={{
            padding: '8px 12px',
            borderRadius: 8,
            background: v('paperDeep'),
            color: v('bad'),
            fontSize: 13,
            border: `1px solid ${v('bad')}`,
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
          {mode !== 'forgot' && (
            <div style={{ fontSize: 11, color: v('inkMute'), lineHeight: 1.4 }}>
              By continuing you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener" style={{ color: v('inkSoft'), textDecoration: 'underline' }}>Terms</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener" style={{ color: v('inkSoft'), textDecoration: 'underline' }}>Privacy Policy</a>.
            </div>
          )}
          {mode === 'forgot' && (
            <button
              type="button"
              onClick={() => { setMode('login'); setError(null); }}
              className="hm-action"
            >Back</button>
          )}
          <button type="submit" className="hm-primary" disabled={submitting}>
            {submitting
              ? (mode === 'login' ? 'Signing in…' : mode === 'signup' ? 'Creating account…' : 'Sending…')
              : (mode === 'login' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Send reset link')}
          </button>
        </div>
      </form>
      )}
    </Modal>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    fontFamily: 'inherit',
    fontSize: 14,
    padding: '8px 12px',
    border: `1px solid ${v('rule')}`,
    borderRadius: 8,
    background: v('paper'),
    color: v('ink'),
    outline: 'none',
  };
}

function oauthBtnStyle(): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    fontSize: 14,
    textDecoration: 'none',
    color: v('ink'),
    background: v('paper'),
    border: `1px solid ${v('rule')}`,
  };
}

function GoogleGlyph() {
  // Google "G" logo (simplified, brand-correct colors)
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

function AppleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M16.365 1.43c0 1.14-.45 2.265-1.193 3.075-.795.87-2.105 1.555-3.155 1.473-.135-1.115.42-2.27 1.155-3.044.83-.87 2.265-1.504 3.193-1.504zm3.985 17.265c-.51 1.183-.755 1.71-1.41 2.755-.915 1.46-2.205 3.28-3.81 3.295-1.425.015-1.79-.93-3.72-.92-1.93.01-2.34.94-3.765.925-1.605-.015-2.83-1.66-3.745-3.12-2.555-4.085-2.825-8.88-1.245-11.43.92-1.485 2.395-2.36 3.78-2.38.5-.005.99.05 1.46.16 2.045.49 2.585.605 3.55-.07.965-.675 2.595-1.135 4.025-.59 1.43.545 2.555 1.585 3.105 2.985-2.79 1.522-2.345 5.53.775 6.39z"/>
    </svg>
  );
}
